import React, { useState } from "react";
import {
	Paper,
	Table,
	TableBody,
	Box,
	Collapse,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Button,
	Stack,
} from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import Chip from "@mui/material/Chip";
import { StyledTableCell, StyledTableRow } from "./Styles/TableAndCellStyles";
import { useNavigate } from "react-router-dom";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDispatch, useSelector } from "react-redux";
import { setTenantPayment } from "state";
import { formatDateString } from "utilities/date";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import axios from "axios";
import { baseURL } from "services/API";
import moment from "moment";
import { withoutSymbolFormatter } from "utilities/currencyFormatter";
import { useEinvoicing } from "services/hooks/einvoicing";
import TopSnack from "components/TopSnack";

const efrisValidationUrl = (paymentOrInvoice) => {
	const receipt = paymentOrInvoice?.efris_receipt || paymentOrInvoice;
	return (
		receipt?.validation_url ||
		(typeof receipt?.qr_code === "string" && receipt.qr_code.startsWith("http")
			? receipt.qr_code
			: "") ||
		""
	);
};

const openEfrisValidation = (url) => {
	if (!url) return;
	window.open(url, "_blank", "noopener,noreferrer");
};

const LandlordTenantPaymentsTable = ({
	paymentData,
	handleDeletePayment,
	handleEditPayment,
	voidablePaymentId,
	onVoidPayment,
	isVoidingPayment,
}) => {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [issuingPaymentId, setIssuingPaymentId] = useState(null);
	const [snackOpen, setSnackOpen] = useState(false);
	const [snackMessage, setSnackMessage] = useState("");
	const [snackSeverity, setSnackSeverity] = useState("success");
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const landlord = useSelector((state) => state.landlord);
	const einvoicingEnabled = Boolean(landlord?.einvoicing_enabled);
	const { issueInvoice } = useEinvoicing();

	const renderStatusPills = (status) => {
		switch (status) {
			case false:
				return (
					<Chip
						variant="outlined"
						sx={{ backgroundColor: "#ffebeb", fontSize: "10px", fontWeight: 700 }}
						label="VOIDED"
						size="small"
						color="warning"
					/>
				);
			case true:
				return (
					<Chip
						variant="outlined"
						sx={{ backgroundColor: "#ebefff", fontSize: "10px", fontWeight: 700 }}
						label="COMPLETE"
						size="small"
						color="primary"
					/>
				);
			default:
				return null;
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleShowReceipt = (periodPayment, individualPayment) => {
		const receiptData = {
			periodPayment: periodPayment,
			individualPayment: individualPayment,
		};

		dispatch(setTenantPayment(receiptData));
		navigate(`/tenant-receipts`);
	};

	function DetailDropDown(props) {
		const { row } = props;
		const [open, setOpen] = useState(false);
		const [itemData, setItemData] = useState([]);

		const handleClickView = async (id) => {
			try {
				const response = await axios.get(
					`${baseURL}/tenants/completed_payments/payments_list?period_id=${id}`
				);
				setItemData(response.data.data);
			} catch (err) {}
		};

		const handleGenerateEfris = async (individualPayment) => {
			if (!individualPayment?.id || !landlord?.id) return;
			setIssuingPaymentId(individualPayment.id);
			try {
				const response = await issueInvoice(landlord.id, {
					payment_id: individualPayment.id,
				});
				const invoice = response?.data;
				const validationUrl = efrisValidationUrl(invoice);
				setItemData((prev) =>
					(prev || []).map((payment) =>
						payment.id === individualPayment.id
							? {
									...payment,
									efris_receipt: {
										id: invoice?.id,
										status: invoice?.status || "posted",
										fiscal_document_number: invoice?.fiscal_document_number,
										verification_code: invoice?.verification_code,
										qr_code: invoice?.qr_code,
										validation_url: validationUrl,
									},
							  }
							: payment
					)
				);
				setSnackSeverity("success");
				setSnackMessage(
					invoice?.fiscal_document_number
						? `EFRIS receipt issued: ${invoice.fiscal_document_number}`
						: response?.message || "EFRIS receipt issued"
				);
				setSnackOpen(true);
			} catch (err) {
				setSnackSeverity("error");
				setSnackMessage(
					err?.response?.data?.error || "Failed to generate EFRIS receipt"
				);
				setSnackOpen(true);
			} finally {
				setIssuingPaymentId(null);
			}
		};

		return (
			<>
				<StyledTableRow>
					<StyledTableCell style={{ padding: "12px 20px" }} align="left">
						{formatDateString(row.related_active_period.date_started)}
					</StyledTableCell>
					<StyledTableCell style={{ padding: "12px 20px" }} align="left">
						{formatDateString(row.related_active_period.date_ended)}
					</StyledTableCell>
					<StyledTableCell style={{ textAlign: "center", padding: "12px 20px" }}>
						<IconButton
							aria-label="expand row"
							onClick={() => {
								setOpen(!open);
								handleClickView(row.related_active_period.id);
							}}
						>
							{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					</StyledTableCell>
					<StyledTableCell style={{ padding: "12px 20px" }} align="left">
						<Button
							sx={{ color: "blue" }}
							onClick={() => handleShowReceipt(row, null)}
						>
							<PaymentIcon color="blue" sx={{ mr: 1 }} />
							View Period Receipt
						</Button>
					</StyledTableCell>
				</StyledTableRow>
				<StyledTableRow>
					<StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
						<Collapse in={open} timeout="auto" unmountOnExit>
							<Box sx={{ margin: "5px", width: "100%" }}>
								<Typography variant="h5" gutterBottom component="div">
									Payment Details
								</Typography>
								{itemData ? (
									<Box sx={{ display: "flex", flexDirection: "column", margin: "10px" }}>
										<Table stickyHeader aria-label="sticky table">
											<TableHead>
												<StyledTableRow>
													<StyledTableCell style={{ padding: "6px 20px" }}>
														Payment Date
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Amount Paid
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Mode of Payment
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Status
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Narration
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Months Covered
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Next Payment Date
													</StyledTableCell>
													<StyledTableCell
														style={{ padding: "6px 20px", textAlign: "center" }}
													>
														Actions
													</StyledTableCell>
												</StyledTableRow>
											</TableHead>
											<TableBody>
												{itemData.length === 0 ? (
													<StyledTableRow>
														<StyledTableCell colSpan={8} style={{ textAlign: "center" }}>
															<Typography variant="h5">No payments recorded</Typography>
														</StyledTableCell>
													</StyledTableRow>
												) : (
													itemData
														.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
														.map((individualPayment) => {
															const uraUrl = efrisValidationUrl(individualPayment);
															const rowCanVoid =
																voidablePaymentId != null &&
																individualPayment.id === voidablePaymentId;
															return (
																<StyledTableRow key={individualPayment.id}>
																	<StyledTableCell style={{ padding: "6px 20px" }}>
																		{moment(
																			individualPayment.date_paid
																				? individualPayment.date_paid
																				: individualPayment.date_created
																		).format("LL")}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{withoutSymbolFormatter(parseFloat(individualPayment.amount))}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{individualPayment.payment_method?.name || "Unknown"}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{renderStatusPills(individualPayment.status)}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{individualPayment.has_security_deposit
																			? "Security Deposit"
																			: individualPayment.is_balance_carried_forward_payment
																			? "Balance Carried Forward"
																			: individualPayment.is_outstanding_balance_payment
																			? "Outstanding Balance"
																			: `${individualPayment.related_month?.month || "January"} ${
																					individualPayment.related_month?.year || "2025"
																			  }`}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{individualPayment.months_covered != null
																			? individualPayment.months_covered
																			: "—"}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		{individualPayment.next_payment_date
																			? moment(individualPayment.next_payment_date).format("LL")
																			: "—"}
																	</StyledTableCell>
																	<StyledTableCell
																		style={{ padding: "6px 20px", textAlign: "center" }}
																	>
																		<Stack
																			direction="row"
																			spacing={1}
																			justifyContent="center"
																			flexWrap="wrap"
																		>
																			<Button
																				sx={{ color: "blue" }}
																				onClick={() => handleShowReceipt(row, individualPayment)}
																			>
																				<PaymentIcon color="blue" sx={{ mr: 1 }} />
																				View Receipt
																			</Button>
																			{einvoicingEnabled &&
																				(uraUrl ? (
																					<Button
																						color="success"
																						size="small"
																						onClick={() => openEfrisValidation(uraUrl)}
																					>
																						<OpenInNewIcon sx={{ mr: 1 }} />
																						View URA Receipt
																					</Button>
																				) : (
																					<Button
																						color="secondary"
																						size="small"
																						disabled={issuingPaymentId === individualPayment.id}
																						onClick={() => handleGenerateEfris(individualPayment)}
																					>
																						<ReceiptLongIcon sx={{ mr: 1 }} />
																						{issuingPaymentId === individualPayment.id
																							? "Issuing…"
																							: "Generate EFRIS"}
																					</Button>
																				))}
																			{rowCanVoid && (
																				<Button
																					color="error"
																					variant="outlined"
																					size="small"
																					disabled={isVoidingPayment}
																					onClick={() => onVoidPayment?.(individualPayment)}
																				>
																					{isVoidingPayment ? "Voiding…" : "Void"}
																				</Button>
																			)}
																		</Stack>
																	</StyledTableCell>
																</StyledTableRow>
															);
														})
												)}
											</TableBody>
										</Table>
									</Box>
								) : (
									<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
										<h4>Payment Details Loading.......</h4>
									</Box>
								)}
							</Box>
						</Collapse>
					</StyledTableCell>
				</StyledTableRow>
			</>
		);
	}

	return (
		<>
			<Paper sx={{ width: "100%", overflow: "hidden" }}>
				<TableContainer>
					<Table>
						<TableHead>
							<StyledTableRow>
								<StyledTableCell align="left">Month Start Date</StyledTableCell>
								<StyledTableCell align="left">Month End Date</StyledTableCell>
								<StyledTableCell align="center">Payments Made</StyledTableCell>
								<StyledTableCell align="left">Actions</StyledTableCell>
							</StyledTableRow>
						</TableHead>
						<TableBody>
							{(paymentData > 0
								? paymentData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								: paymentData
							).map((payment) => (
								<DetailDropDown key={payment.id} row={payment} />
							))}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TablePagination
									rowsPerPageOptions={[10, 25, { label: "All", value: -1 }]}
									colSpan={7}
									count={paymentData.length}
									rowsPerPage={rowsPerPage}
									page={page}
									SelectProps={{
										inputProps: {
											"aria-label": "rows per page",
										},
										native: true,
									}}
									onPageChange={handleChangePage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									ActionsComponent={TablePaginationActions}
								/>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			</Paper>
			<TopSnack
				open={snackOpen}
				handleCloseSnack={() => setSnackOpen(false)}
				message={snackMessage}
				severity={snackSeverity}
			/>
		</>
	);
};

export default LandlordTenantPaymentsTable;
